import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { API_BASE_URL, ApiRequestError } from './api';

interface User {
  id: string;
  email: string;
  name: string;
  emailVerified: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (
    name: string,
    email: string,
    password: string,
    confirmPassword: string
  ) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

function getStoredAccessToken(): string | null {
  return localStorage.getItem('accessToken');
}

function storeAccessToken(token: string): void {
  localStorage.setItem('accessToken', token);
}

function clearTokens(): void {
  localStorage.removeItem('accessToken');
}

export function getAccessToken(): string | null {
  return localStorage.getItem('accessToken');
}

async function authFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const response = await fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });
  if (response.status === 204) {
    return undefined as T;
  }
  const data = await response.json();
  if (!response.ok) {
    throw new ApiRequestError(
      data.error?.message || `Request failed with status ${response.status}`,
      response.status,
      data.error?.code,
      data.error?.details
    );
  }
  return data as T;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = getStoredAccessToken();
    if (!token) {
      setIsLoading(false);
      return;
    }
    try {
      const res = await authFetch<{
        success: boolean;
        data: {
          user: { user_id: string; email: string; name: string | null; email_verified: boolean };
        };
      }>('/auth/me');
      const u = res.data.user;
      setUser({
        id: u.user_id,
        email: u.email,
        name: u.name ?? '',
        emailVerified: u.email_verified ?? false,
      });
    } catch {
      clearTokens();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await authFetch<{
      success: boolean;
      data: {
        user: { id: string; email: string; name: string | null; email_verified: boolean };
        accessToken: string;
      };
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    storeAccessToken(res.data.accessToken);
    const u = res.data.user;
    setUser({
      id: u.id,
      email: u.email,
      name: u.name ?? '',
      emailVerified: u.email_verified ?? false,
    });
  }, []);

  const register = useCallback(
    async (name: string, email: string, password: string, confirmPassword: string) => {
      const res = await authFetch<{
        success: boolean;
        data: {
          user: { id: string; email: string; name: string | null; email_verified: boolean };
          accessToken: string;
        };
      }>('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password, confirmPassword, name }),
      });
      storeAccessToken(res.data.accessToken);
      const u = res.data.user;
      setUser({
        id: u.id,
        email: u.email,
        name: u.name ?? '',
        emailVerified: u.email_verified ?? false,
      });
    },
    []
  );

  const logout = useCallback(() => {
    clearTokens();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated: !!user, isLoading, login, register, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
